import React, { useState, useRef, useEffect, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';

export const CustomHorizontalScrollbar = forwardRef(({ 
  children, 
  width = '100%',
  height = 'auto',
  className = '',
  showScrollbar = true,
  scrollbarHeight = '8px',
  thumbColor = 'rgba(0, 0, 0, 0.4)',
  trackColor = '#f5f5f5',
  onScroll,
}, ref) => {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  
  const scrollRatio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
  const thumbWidth = Math.max(scrollRatio * clientWidth, 30);
  const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);
  const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * (clientWidth - thumbWidth) : 0;
  
  const hasScrollbar = scrollWidth > clientWidth && showScrollbar;

  useImperativeHandle(ref, () => ({
    scrollToStart: () => scrollToStart(),
    scrollToEnd: () => scrollToEnd(),
    scrollTo: (position) => scrollTo(position),
    scrollBy: (amount) => scrollBy(amount),
    getScrollInfo: () => ({
      scrollLeft,
      scrollWidth,
      clientWidth,
      maxScrollLeft,
      scrollPercentage: maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * 100 : 0
    })
  }), [scrollLeft, scrollWidth, clientWidth, maxScrollLeft]);

  const scrollToStart = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    container.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  };

  const scrollToEnd = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    container.scrollTo({
      left: scrollWidth - clientWidth,
      behavior: 'smooth'
    });
  };

  const scrollTo = (position) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const clampedPosition = Math.max(0, Math.min(scrollWidth - clientWidth, position));
    container.scrollTo({
      left: clampedPosition,
      behavior: 'smooth'
    });
  };

  const scrollBy = (amount) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const newPosition = scrollLeft + amount;
    const clampedPosition = Math.max(0, Math.min(scrollWidth - clientWidth, newPosition));
    container.scrollTo({
      left: clampedPosition,
      behavior: 'smooth'
    });
  };

  const updateScrollInfo = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const newScrollLeft = container.scrollLeft;
    const newScrollWidth = container.scrollWidth;
    const newClientWidth = container.clientWidth;
    
    setScrollLeft(newScrollLeft);
    setScrollWidth(newScrollWidth);
    setClientWidth(newClientWidth);
    setIsInitialized(true);
    
    if (onScroll) {
      onScroll({
        scrollLeft: newScrollLeft,
        scrollWidth: newScrollWidth,
        clientWidth: newClientWidth,
        maxScrollLeft: Math.max(newScrollWidth - newClientWidth, 0),
        scrollPercentage: newScrollWidth > newClientWidth 
          ? (newScrollLeft / Math.max(newScrollWidth - newClientWidth, 1)) * 100 
          : 0
      });
    }
  };

  useLayoutEffect(() => {
    updateScrollInfo();
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const timeoutId = setTimeout(() => {
      updateScrollInfo();
    }, 0);
    
    const handleScroll = () => {
      updateScrollInfo();
    };
    
    const resizeObserver = new ResizeObserver(() => {
      updateScrollInfo();
    });
    
    resizeObserver.observe(container);
    
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateScrollInfo);
    
    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollInfo);
      resizeObserver.disconnect();
      clearTimeout(window.horizontalScrollbarTimer);
    };
  }, [onScroll]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const mutationObserver = new MutationObserver(() => {
      setTimeout(updateScrollInfo, 10);
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, []);
  
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startScrollLeft = scrollLeft;
    
    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaScrollLeft = (deltaX / clientWidth) * scrollWidth;
      const newScrollLeft = Math.max(0, Math.min(scrollWidth - clientWidth, startScrollLeft + deltaScrollLeft));
      
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = newScrollLeft;
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  const handleTrackClick = (e) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    if (e.target !== e.currentTarget) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const scrollPercentage = clickX / trackWidth;
    const newScrollLeft = scrollPercentage * (scrollWidth - clientWidth);
    
    container.scrollLeft = Math.max(0, Math.min(scrollWidth - clientWidth, newScrollLeft));
  };
  
  return (
    <div 
      className={`relative ${className} custom-h-scroll`}
      style={{ width, height }}
    >
      <div
        ref={scrollContainerRef}
        className="w-full h-full overflow-auto custom-h-scroll"
        style={{
          paddingBottom: hasScrollbar ? '12px' : '0',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <style jsx>{`
          .custom-h-scroll::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {children}
      </div>

      {hasScrollbar && isInitialized && (
        <div
          className="absolute left-0 bottom-0 transition-opacity duration-300 opacity-100"
          style={{
            width: '100%',
            height: scrollbarHeight,
            backgroundColor: trackColor,
            borderRadius: scrollbarHeight,
            cursor: 'pointer'
          }}
          onClick={handleTrackClick}
        >
          <div
            ref={scrollThumbRef}
            className="tw-relative tw-transition-colors tw-duration-200 tw-hover:tw-bg-opacity-60"
            style={{
              height: '100%',
              width: `${thumbWidth}px`,
              left: `${isNaN(thumbLeft) ? 0 : thumbLeft}px`,
              backgroundColor: thumbColor,
              borderRadius: scrollbarHeight,
              cursor: 'grab',
              minWidth: '30px'
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
});
